import { useQuery } from "@apollo/client/react";
import type { TFunction } from "i18next";
import {
  ConversationStatus,
  type GetTicketStatusesQuery,
  type TicketStatusColor,
  TicketStatusKey,
} from "@lytra/frontend-common/__generated__/graphql";
import { GET_TICKET_STATUSES } from "./ticketQueries";
import { STATUS_ORDER, statusLabel } from "./ticketHelpers";

/** One of the tenant's ticket statuses, a flat ordered list (ADR-0078). `category` is its
 * hidden SLA behaviour, which its tickets carry as their `status`. */
export type TicketStatusView = GetTicketStatusesQuery["ticketStatuses"][number];

/**
 * One status a ticket can be in. `value` is `CATEGORY:id`: every picker, filter and
 * board column reads the category off the value alone, and the format is the one stored
 * filters already hold. A bare `CATEGORY` is a filter stored before ADR-0078.
 */
export interface StatusChoice {
  value: string;
  status: ConversationStatus;
  ticketStatusId: string | null;
  label: string;
  color: TicketStatusColor | null;
}

export const statusChoiceValue = (
  status: ConversationStatus,
  ticketStatusId?: string | null,
): string => (ticketStatusId ? `${status}:${ticketStatusId}` : status);

const CATEGORIES: readonly string[] = STATUS_ORDER;
const MERGED_NEW: string = ConversationStatus.New;

/** What a choice value selects, or null for anything else (a stale stored filter). NEW was
 * merged into OPEN, so a NEW value reads as OPEN. */
export function parseStatusChoice(
  value: string | null | undefined,
): { status: ConversationStatus; ticketStatusId: string | null } | null {
  if (!value) return null;
  const [category, id] = value.split(":", 2);
  if (category === MERGED_NEW)
    return { status: ConversationStatus.Open, ticketStatusId: null };
  if (!CATEGORIES.includes(category)) return null;
  return {
    status: category as ConversationStatus,
    ticketStatusId: id || null,
  };
}

/** Every status a ticket can be put in, in the tenant's order. */
export function statusChoices(
  statuses: readonly TicketStatusView[],
): StatusChoice[] {
  return statuses.map((status) => ({
    value: statusChoiceValue(status.category, status.id),
    status: status.category,
    ticketStatusId: status.id,
    label: status.name,
    color: status.color,
  }));
}

/** The status a choice value names, by its id: a status's category may have changed
 * since the value was stored. */
export function choiceOf(
  choices: readonly StatusChoice[],
  value: string | null | undefined,
): StatusChoice | undefined {
  const id = parseStatusChoice(value)?.ticketStatusId;
  return id
    ? choices.find((choice) => choice.ticketStatusId === id)
    : undefined;
}

/** What picking a choice sends to `conversationUpdate`: the status and its category. */
export const statusChange = (
  choice: StatusChoice | undefined,
): { status?: ConversationStatus; ticketStatusId?: string | null } =>
  choice
    ? { status: choice.status, ticketStatusId: choice.ticketStatusId }
    : {};

/**
 * A stored filter brought up to the loaded statuses. One stored before ADR-0078 named a
 * category alone ("Ausstehend" meant PENDING): it becomes the default status that
 * carried that name, the one whose key is the category. A status whose SLA behaviour
 * changed gets its current category; a deleted one clears the filter. Until the
 * statuses are loaded the value stays as it is.
 */
export function upgradeStatusChoice(
  value: string | null,
  statuses: readonly TicketStatusView[],
): string | null {
  const picked = parseStatusChoice(value);
  if (!picked || statuses.length === 0) return value;
  if (picked.ticketStatusId) {
    const own = ticketStatusOf(statuses, picked.ticketStatusId);
    return own ? statusChoiceValue(own.category, own.id) : null;
  }
  const key = NAMED_BY_CATEGORY[picked.status];
  const named = key && statuses.find((status) => status.systemKey === key);
  return named ? statusChoiceValue(named.category, named.id) : value;
}

/** The default status each category's name belonged to before ADR-0078. */
const NAMED_BY_CATEGORY: Partial<Record<ConversationStatus, TicketStatusKey>> =
  {
    [ConversationStatus.Open]: TicketStatusKey.Open,
    [ConversationStatus.Pending]: TicketStatusKey.Pending,
    [ConversationStatus.OnHold]: TicketStatusKey.OnHold,
    [ConversationStatus.Closed]: TicketStatusKey.Closed,
  };

/** A ticket's status row, once the statuses are loaded. */
export function ticketStatusOf(
  statuses: readonly TicketStatusView[],
  ticketStatusId?: string | null,
): TicketStatusView | undefined {
  return ticketStatusId
    ? statuses.find((candidate) => candidate.id === ticketStatusId)
    : undefined;
}

/** A ticket's status as people read it: its status's name, else the category's. */
export function ticketStatusName(
  t: TFunction,
  statuses: readonly TicketStatusView[],
  status: ConversationStatus,
  ticketStatusId?: string | null,
): string {
  return (
    ticketStatusOf(statuses, ticketStatusId)?.name ?? statusLabel(t, status)
  );
}

/** The tenant's statuses; empty until loaded. */
export function useTicketStatuses(): readonly TicketStatusView[] {
  const { data } = useQuery(GET_TICKET_STATUSES, { errorPolicy: "all" });
  return data?.ticketStatuses ?? [];
}
